
import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getUserProducts, addUserProduct, updateUserProduct, deleteUserProduct } from '@/services/products.service';
import { networkService } from '@/services/network.service';
import { productRepository } from '@/repositories/product.repository';
import { offlineDb } from '@/db/offlineDb';
import { enhancedImageCacheService } from '@/services/offline/image-cache-enhanced.service';
import { productSyncService } from '@/services/products/productSyncService';
import { syncService } from '@/services/sync.service';
import OfflineLogger from '@/utils/offlineLogger';
import { getValidUserId, isValidUUID } from '@/utils/validation';
import { UserProduct } from '@/types';
import { normalizePhotoUrl } from '@/utils/normalizePhotoUrl';
import { categoryOperationsService } from '@/services/categories/categoryOperations.service';

export function useOfflineProducts() {
  const [isOnline, setIsOnline] = useState(networkService.isOnline());
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncLock, setSyncLock] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [hasInitialSyncCompleted, setHasInitialSyncCompleted] = useState(false);
  const queryClient = useQueryClient();
  
  // ✅ NUOVO: Timestamp per tracking delle operazioni
  const [lastSyncTimestamp, setLastSyncTimestamp] = useState(0);
  
  // Update online status when network changes
  useEffect(() => {
    const handleNetworkChange = (status: { connected: boolean }) => {
      const wasOffline = !isOnline;
      setIsOnline(status.connected);
      
      // ✅ MIGLIORATO: Sync ottimizzato quando torniamo online
      if (status.connected && wasOffline && !syncLock) {
        //OfflineLogger.info('useOfflineProducts', 'Back online - scheduling optimized sync with deduplication');
        
        // Debounce più lungo per evitare sync multipli
        setTimeout(() => {
          if (!syncLock && !isSyncing) {
            performIncrementalSync();
          }
        }, 3000);
      }
    };
    
    const unsubscribe = networkService.addListener(handleNetworkChange);
    return () => unsubscribe();
  }, [isOnline, syncLock, isSyncing]);

  // ✅ NUOVO: Controllo se il database locale ha prodotti
  const checkLocalProducts = async (): Promise<boolean> => {
    try {
      const localProducts = await productRepository.getAll();
      return localProducts.length > 0;
    } catch (error) {
      OfflineLogger.error('useOfflineProducts.checkLocalProducts', error);
      return false;
    }
  };

  // Check if products entity actually needs synchronization
  const needsProductSync = async (): Promise<boolean> => {
    try {
      const userId = getValidUserId();
      if (!userId) return false;
      return await syncService.isEntitySyncNeeded('products', userId);
    } catch (error) {
      OfflineLogger.error('useOfflineProducts.needsProductSync', error);
      return false;
    }
  };

  // ✅ NUOVO: Force sync quando si entra nella pagina prodotti
  const forceInitialSync = async () => {
    if (!networkService.isOnline() || isSyncing || syncLock || hasInitialSyncCompleted) {
      //OfflineLogger.info('useOfflineProducts.forceInitialSync', 'Skipping initial sync - conditions not met');
      return;
    }

    const needsSync = await needsProductSync();
    if (!needsSync) {
      //OfflineLogger.info('useOfflineProducts.forceInitialSync', 'No product sync needed');
      const localProducts = await productSyncService.loadLocalProducts();
      setHasInitialSyncCompleted(true);
      setIsFirstLoad(false);
      return localProducts;
    }

    setSyncLock(true);
    setIsSyncing(true);

    try {
      //OfflineLogger.info('useOfflineProducts.forceInitialSync', 'Starting forced initial sync from server');

      // Forza il sync dal server e salva tutto nel DB locale
      const serverProducts = await productSyncService.syncFromServer();
      
      // ✅ NUOVO: Assicurati che tutti i prodotti siano salvati nel DB locale
      for (const product of serverProducts) {
        try {
          await productRepository.create(product);
        } catch (error) {
          // Ignora errori di duplicati, ma logga altri errori
          if (!error.toString().includes('duplicate')) {
            //OfflineLogger.error('useOfflineProducts.forceInitialSync', `Error saving product ${product.id}: ${error}`);
          }
        }
      }
      
      // ✅ NUOVO: Preload immagini in background
      const validProducts = serverProducts.filter(p => 
        p.photo && 
        !p.photo.includes('product-photos/https://') &&
        !p.photo.startsWith('blob:') &&
        !p.photo.startsWith('data:') &&
        p.photo.startsWith('http')
      );
      
      if (validProducts.length > 0) {
        enhancedImageCacheService.preloadImages(
          validProducts.map(p => ({ id: p.id, photo: p.photo }))
        );
      }
      
      // Aggiorna la cache
      queryClient.setQueryData(['user-products'], serverProducts);
      setLastSyncTimestamp(Date.now());
      setHasInitialSyncCompleted(true);
      setIsFirstLoad(false);
      
      //OfflineLogger.info('useOfflineProducts.forceInitialSync', `Initial sync completed: ${serverProducts.length} products synced and saved locally`);
      
      return serverProducts;
    } catch (error) {
      OfflineLogger.error('useOfflineProducts.forceInitialSync', error);
      // Fallback a prodotti locali se il server fallisce
      const localProducts = await productSyncService.loadLocalProducts();
      setIsFirstLoad(false);
      return localProducts;
    } finally {
      setIsSyncing(false);
      setSyncLock(false);
    }
  };

  // ✅ NUOVO: Sync incrementale intelligente con deduplicazione
  const performIncrementalSync = async () => {
    if (!networkService.isOnline() || isSyncing || syncLock) return;
    
    setSyncLock(true);
    setIsSyncing(true);
    
    try {
      //OfflineLogger.info('useOfflineProducts', 'Starting incremental sync with deduplication');
      
      // Usa il servizio di sync ottimizzato
      const syncedProducts = await productSyncService.performFullSync();
      
      // ✅ NUOVO: Aggiorna la cache solo se ci sono cambiamenti
      queryClient.setQueryData(['user-products'], syncedProducts);
      setLastSyncTimestamp(Date.now());
      
      //OfflineLogger.info('useOfflineProducts', `Incremental sync completed with ${syncedProducts.length} products`);
      
    } catch (error) {
      OfflineLogger.error('useOfflineProducts.performIncrementalSync', error);
    } finally {
      setIsSyncing(false);
      setSyncLock(false);
    }
  };

  // ✅ MIGLIORATO: Sync pending changes con deduplicazione
  const syncPendingChanges = async () => {
    if (!networkService.isOnline() || isSyncing || syncLock) return;
    
    setSyncLock(true);
    setIsSyncing(true);
    
    try {
      //OfflineLogger.info('useOfflineProducts', 'Starting optimized sync of pending changes');
      
      await productSyncService.syncPendingOperations();
      
      // ✅ NUOVO: Refresh dei dati più intelligente
      const freshProducts = await productSyncService.syncFromServer();
      queryClient.setQueryData(['user-products'], freshProducts);
      setLastSyncTimestamp(Date.now());
      
      //OfflineLogger.info('useOfflineProducts', 'Pending changes sync completed successfully');
      
    } catch (error) {
      OfflineLogger.error('useOfflineProducts.syncPendingChanges', error);
    } finally {
      setIsSyncing(false);
      setSyncLock(false);
    }
  };

  // Helper to identify offline-created products
  const isOfflineCreatedProduct = (id: string): boolean => {
    return id.startsWith('offline-') || (id.includes('-') && id.length > 30);
  };
  
  // ✅ MIGLIORATO: Product fetching ottimizzato per primo accesso
  const { data: products, isLoading, error, refetch } = useQuery({
    queryKey: ['user-products'],
    queryFn: async () => {
      // ✅ FIX: Garantisce sempre dei prodotti base disponibili
      const hasLocalProducts = await checkLocalProducts();
      
      if (networkService.isOnline()) {
        try {
          //OfflineLogger.info('useOfflineProducts', 'Fetching products online');
          
          // ✅ FIX: Force sync only on first load or if no local products
          if ((isFirstLoad && !hasInitialSyncCompleted) || !hasLocalProducts) {
            return await forceInitialSync() || [];
          }
          
          // ✅ Fetch normale da server con fallback locale
          const onlineProducts = await getUserProducts();
          
          // ✅ Cache products locally in background
          setTimeout(async () => {
            try {
              for (const product of onlineProducts) {
                await productRepository.create(product);
              }
              //OfflineLogger.info('useOfflineProducts', `Cached ${onlineProducts.length} products locally`);
            } catch (error) {
              OfflineLogger.error('useOfflineProducts.cacheProducts', error);
            }
          }, 100);
          
          setIsFirstLoad(false);
          return onlineProducts;
        } catch (error) {
          OfflineLogger.error('useOfflineProducts.fetchOnline', error);
          // ✅ Fallback a dati locali se fetch online fallisce
          const localProducts = await productSyncService.loadLocalProducts();
          setIsFirstLoad(false);
          return localProducts;
        }
      } else {
        // ✅ Modalità offline: carica i prodotti locali
        //OfflineLogger.info('useOfflineProducts', 'Offline mode: loading local products');

        const localProducts = await productSyncService.loadLocalProducts();

        setIsFirstLoad(false);
        return localProducts;
      }
    },
    staleTime: isOnline ? 1000 * 60 * 5 : Infinity, // ✅ 5 min online, infinito offline
    retry: (failureCount, error) => {
      // ✅ Solo 1 retry se online, nessuno se offline
      return isOnline && failureCount < 1;
    }
  });



  // ✅ NEW: Create product function
  const createProduct = async (productData: {
    userId: string;
    name: string;
    defaultQuantity: number;
    defaultUnit: string;
    barcode?: string;
    photo?: string;
    openfoodfactsId?: string;
    categoryId?: string;
    attributes: any[];
  }) => {
    try {
      if (!productData.categoryId || !isValidUUID(productData.categoryId)) {
        productData.categoryId = (await categoryOperationsService.getOrCreateCategory('Altro')).id;
      }

      const newProduct: UserProduct = {
        id: crypto.randomUUID(),
        name: productData.name,
        userId: productData.userId,
        defaultQuantity: productData.defaultQuantity,
        defaultUnit: productData.defaultUnit,
        categoryId: productData.categoryId,
        barcode: productData.barcode || null,
        photo: productData.photo || null,
        openfoodfacts_id: productData.openfoodfactsId || null,
        attributes: productData.attributes || [],
        createdAt: new Date().toISOString()
      };

      if (isOnline) {
        const created = await addUserProduct(newProduct);
        queryClient.invalidateQueries({ queryKey: ['user-products'] });
        return created;
      } else {
        // Save offline
        await productRepository.create(newProduct);
        queryClient.setQueryData(['user-products'], (old: UserProduct[] | undefined) => 
          old ? [...old, newProduct] : [newProduct]
        );
        return newProduct;
      }
    } catch (error) {
      OfflineLogger.error('useOfflineProducts.createProduct', error);
      throw error;
    }
  };

  // ✅ NEW: Update product function
  const updateProduct = async (id: string, updates: Partial<UserProduct>) => {
    try {
      if (isOnline) {
        const updated = await updateUserProduct(id, updates);

        // keep offline DB in sync when online
        await productRepository.update(id, updates);
        await productRepository.markAsSynced([id]);

        // update cached data immediately
        queryClient.setQueryData(['user-products'], (old: UserProduct[] | undefined) =>
          old ? old.map(p => p.id === id ? { ...p, ...updates } : p) : []
        );

        return updated;
      } else {
        // Update offline
        await productRepository.update(id, updates);
        queryClient.setQueryData(['user-products'], (old: UserProduct[] | undefined) =>
          old ? old.map(p => p.id === id ? { ...p, ...updates } : p) : []
        );
        return { id, ...updates };
      }
    } catch (error) {
      OfflineLogger.error('useOfflineProducts.updateProduct', error);
      throw error;
    }
  };

  // ✅ NEW: Delete product function
  const deleteProduct = async (id: string): Promise<UserProduct | null> => {
    try {
      if (isOnline) {
        const deleted = await deleteUserProduct(id);
        await productRepository.physicalDelete(id);
        queryClient.setQueryData(['user-products'], (old: UserProduct[] | undefined) =>
          old ? old.filter(p => p.id !== id) : []
        );
        queryClient.invalidateQueries({ queryKey: ['user-products'] });
        return deleted;
      } else {
        // Fetch product before marking as deleted
        const existing = await offlineDb.products.get(id);
        let deletedProduct: UserProduct | null = null;
        if (existing) {
          deletedProduct = {
            id: existing.id,
            userId: existing.user_id || '',
            name: existing.name,
            defaultQuantity: existing.default_quantity,
            defaultUnit: existing.default_unit,
            photo: normalizePhotoUrl(existing.photo),
            barcode: existing.barcode,
            openfoodfacts_id: existing.openfoodfacts_id,
            attributes: existing.attributes || [],
            categoryId: existing.category_id,
            createdAt: existing.created_at
          };
        }

        // Delete offline
        await productRepository.delete(id);
        queryClient.setQueryData(['user-products'], (old: UserProduct[] | undefined) =>
          old ? old.filter(p => p.id !== id) : []
        );
        return deletedProduct;
      }
    } catch (error) {
      OfflineLogger.error('useOfflineProducts.deleteProduct', error);
      throw error;
    }
  };

  return {
    products: products || [],
    isLoading,
    error,
    refetch,
    refreshProducts: refetch,
    isOnline,
    isSyncing,
    isFirstLoad,
    hasInitialSyncCompleted,
    syncPendingChanges,
    forceSync: performIncrementalSync,
    performIncrementalSync,
    forceInitialSync,
    isOfflineCreatedProduct,
    lastSyncTimestamp,
    createProduct,
    updateProduct,
    deleteProduct
  };
}
